import { InvoiceStatusFilter } from "@bezbolisno/contracts/invoices";
import { t } from "@bezbolisno/i18n";
import { can } from "@bezbolisno/kit/rbac";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { InvoicesScreen } from "../../../screens/invoices/invoices-screen";
import { invoiceListQuery } from "../../../screens/invoices/queries";
import { LoadErrorScreen } from "../../../screens/invoices/shared";

const Search = z.object({
  status: InvoiceStatusFilter.optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
});

const filtersOf = (s: z.infer<typeof Search>) => ({
  status: s.status ?? ("unpaid" as const),
  q: s.q || undefined,
  page: s.page ?? 1,
});

export const Route = createFileRoute("/_authed/invoices/")({
  validateSearch: Search,
  beforeLoad: ({ context }) => {
    if (!can(context.session.role, "invoice:manage")) throw redirect({ to: "/" });
  },
  loaderDeps: ({ search }) => filtersOf(search),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(invoiceListQuery(deps)),
  errorComponent: () => <LoadErrorScreen title={t.invoices.title} />,
  component: InvoicesRoute,
});

function InvoicesRoute() {
  const search = Route.useSearch();
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();
  const filters = filtersOf(search);
  return (
    <InvoicesScreen
      filters={filters}
      role={session.role}
      onApply={(next) => {
        const merged = { status: filters.status, q: filters.q ?? "", ...next };
        navigate({
          to: "/invoices",
          search: {
            status: merged.status === "unpaid" ? undefined : merged.status,
            q: merged.q || undefined,
            page: undefined,
          },
        });
      }}
    />
  );
}
